import Container from '@/components/Container';
import React, { FC, useEffect, useState } from 'react';
import { Page } from '@/declarations/models/Page';
import { useStore } from '@/components/store/Store';
import { Api } from '@/services/Api';
import Loader from '@/components/Loader';
import { calculateObjectPosition } from '@/components/Image';
import { Button, Typography } from '@mui/material';
import { MaterialSymbol } from '@/components/MaterialSymbol';
import Styles from '@/assets/js/Styles';
import { useTranslation } from 'react-i18next';

export const WelcomeBannerWidget: FC<{ siteUrl: string | undefined }> = ({ siteUrl }) => {
  const { t: tComponents } = useTranslation('components');
  const [frontpage, setFrontpage] = useState<Page>();
  const [loading, setLoading] = useState(true);
  const {
    state: { selectedSiteDefaultPageId, selectedSiteData, selectedSite, selectedSiteLanguage, currentUser },
  } = useStore();
  const currentUserRole = currentUser?.current_site_role;
  useEffect(() => {
    if (selectedSiteDefaultPageId) {
      setLoading(true);
      Api.getPage(selectedSiteDefaultPageId)
        .fetchDirect(undefined)
        .then(setFrontpage)
        .finally(() => setLoading(false));
    }
  }, [selectedSiteDefaultPageId]);

  // console.log('selectedSiteData', selectedSiteData);

  if (loading) {
    return (
      <Container
        fullWidth
        fullHeight
        top
        sx={{
          minHeight: '300px',
          aspectRatio: '16/9',
        }}>
        <Loader />
      </Container>
    );
  }

  return (
    <Container
      fullWidth
      fullHeight
      top
      left
      sx={{
        minHeight: '500px',
        maxHeight: '700px',
        aspectRatio: '16/9',
        borderRadius: Styles.Dimensions.RADIUS_ROUNDNESS_DEFAULT,
      }}>
      {!frontpage && (
        <Container fullWidth top left>
          -
        </Container>
      )}
      {frontpage && (
        <Container
          fullWidth
          fullHeight
          column
          top
          left
          sx={{
            backgroundImage: frontpage.image_src ? `url(${frontpage.image_src}?dimension=max)` : undefined,
            backgroundSize: 'cover',
            backgroundPosition: calculateObjectPosition(frontpage.image?.x, frontpage.image?.y) ?? 'center',
            backgroundRepeat: 'no-repeat',
            borderRadius: Styles.Dimensions.RADIUS_ROUNDNESS_DEFAULT,
          }}>
          <Container
            column
            left
            spaceBetween
            sx={{
              backgroundColor: 'rgba(248,248,248,0.9)',
              padding: '1rem',
              color: 'black',
              height: '100%',
              width: 'fit-content',
              maxWidth: '50%',
            }}>
            <Container left column>
              <Container left gap={1.5} mb={1} top>
                {selectedSiteData?.content?.settings?.favicon && (
                  <img
                    src={`${selectedSiteData.content?.settings?.favicon.url}?dimension=100x100`}
                    alt='Favicon'
                    title='Favicon'
                    style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                  />
                )}
                {!selectedSiteData?.content?.settings?.favicon && (
                  <MaterialSymbol sx={{ fontSize: '40px' }} name='public' />
                )}
                <Typography
                  sx={{
                    fontSize: '1.65rem',
                    fontWeight: 400,
                  }}>
                  {selectedSite?.name} ({selectedSiteLanguage?.toLocaleUpperCase()})
                </Typography>
              </Container>
              {siteUrl && (
                <a
                  href={siteUrl}
                  style={{ color: '#000', fontSize: '1.1rem', wordBreak: 'break-word' }}
                  target='_blank'
                  rel='noreferrer'>
                  {siteUrl}
                </a>
              )}
              Site-ID: {selectedSite?.id} <br />
              {!selectedSiteData?.content?.settings?.favicon && (
                <i>{tComponents('DashboardView.WelcomeBannerWidget.NoFavicon')}</i>
              )}
              {frontpage.description && <p style={{ maxWidth: '600px' }}>{frontpage.description}</p>}
              {!frontpage.description && <i>{tComponents('DashboardView.WelcomeBannerWidget.NoDescription')}</i>}
              {!frontpage.image && <i>{tComponents('DashboardView.WelcomeBannerWidget.NoImage')}</i>}
            </Container>
            <Container left column>
              {currentUserRole !== 'user' && (
                <>
                  <Button
                    startIcon={<MaterialSymbol name='edit' />}
                    fullWidth
                    href={`/editor/${frontpage.id}`}
                    variant='contained'
                    color='primary'>
                    {tComponents('DashboardView.WelcomeBannerWidget.DescriptionLabel')}
                  </Button>
                  <Button
                    startIcon={<MaterialSymbol name='edit' />}
                    fullWidth
                    href={`/editor/${frontpage.id}`}
                    variant='contained'
                    color='primary'>
                    {tComponents('DashboardView.WelcomeBannerWidget.ImageLabel')}
                  </Button>
                </>
              )}
            </Container>
          </Container>
        </Container>
      )}
    </Container>
  );
};
